
import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild, signal, ChangeDetectionStrategy } from '@angular/core';

import { TranslatePipe } from '@app/common-codes/components/directives/TranslatePipe';

@Component({
    selector: 'app-photo-capture',
    standalone: true,
    imports: [TranslatePipe],
    templateUrl: './photo-capture.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styles: ``
})
export class PhotoCaptureComponent implements OnInit, OnDestroy {

    @ViewChild('videoElement') videoElement!: ElementRef<HTMLVideoElement>;
    @ViewChild('canvasElement') canvasElement!: ElementRef<HTMLCanvasElement>;

    @Input() showSilhouette = true;
    /** Cle de traduction des instructions affichees sous le bouton de capture. */
    @Input() instructionsKey = 'vues.ipad.checkin.photo.instructions';

    @Output() photoCaptured = new EventEmitter<string>();  // base64 data URL
    @Output() photoFile = new EventEmitter<File>();         // File object

    stream: MediaStream | null = null;
    capturedImage = signal<string | null>(null);
    countdown = signal<number | null>(null);
    cameraReady = signal(false);
    cameraError = signal(false);

    ngOnInit(): void {
        this.startCamera();
    }

    ngOnDestroy(): void {
        this.stopCamera();
    }

    async startCamera(): Promise<void> {
        try {
            this.cameraError.set(false);
            this.stream = await navigator.mediaDevices.getUserMedia({
                video: { facingMode: 'user', width: { ideal: 640 }, height: { ideal: 480 } }
            });
            this.attachStream();
        } catch (err) {
            console.error('Camera access error:', err);
            this.cameraError.set(true);
        }
    }

    /** Le <video> peut ne pas etre encore rendu : on retente plutot que d'attendre un delai fixe. */
    private attachStream(attempt = 0): void {
        const video = this.videoElement?.nativeElement;
        if (!video) {
            if (attempt < 20) {
                setTimeout(() => this.attachStream(attempt + 1), 50);
            }
            return;
        }
        video.srcObject = this.stream;
        video.play();
        this.cameraReady.set(true);
    }

    stopCamera(): void {
        if (this.stream) {
            this.stream.getTracks().forEach(track => track.stop());
            this.stream = null;
        }
    }

    startCountdown(): void {
        if (this.countdown() !== null) return; // déjà en cours
        this.countdown.set(3);

        const interval = setInterval(() => {
            const current = this.countdown();
            if (current !== null && current > 1) {
                this.countdown.set(current - 1);
            } else {
                clearInterval(interval);
                this.countdown.set(null);
                this.takePhoto();
            }
        }, 1000);
    }

    takePhoto(): void {
        const video = this.videoElement?.nativeElement;
        const canvas = this.canvasElement?.nativeElement;
        if (!video || !canvas) return;

        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        // Miroir horizontal pour selfie
        ctx.translate(canvas.width, 0);
        ctx.scale(-1, 1);
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        ctx.setTransform(1, 0, 0, 1, 0, 0); // reset

        const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
        this.capturedImage.set(dataUrl);

        // Émettre le base64
        this.photoCaptured.emit(dataUrl);

        // Émettre aussi un File
        canvas.toBlob((blob) => {
            if (blob) {
                const file = new File([blob], `photo_${Date.now()}.jpg`, { type: 'image/jpeg' });
                this.photoFile.emit(file);
            }
        }, 'image/jpeg', 0.85);

        this.stopCamera();
    }

    retakePhoto(): void {
        this.capturedImage.set(null);
        this.startCamera();
    }
}